// Small pieces shared by the sales replay screens.

import type { ReplayRunSummary } from '@enp/domain/api';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  StatusBadge,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { meQueryOptions } from '../auth/session.js';
import { ApiError } from '../lib/api.js';
import { runStatusOf } from './labels.js';
import { replayKeys, runningRunOf, startRun } from './queries.js';

export function RunStatusBadge({ run }: { run: Pick<ReplayRunSummary, 'status' | 'error_code'> }) {
  const s = runStatusOf(run);
  return <StatusBadge status={s.status}>{s.label}</StatusBadge>;
}

/** Starting a replay reads the source and writes a practice branch: catalogue editors only. */
export function useCanRunReplays(): boolean {
  const { data: me } = useQuery(meQueryOptions());
  return me?.branch_scope === null && (me?.permissions.includes('catalogue.edit') ?? false);
}

/** "Sales replay › <page>" above a page title. */
export function ReplayBreadcrumb({ page }: { page: string }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/replay">Sales replay</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{page}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * Starts a run of a scenario and opens it. A run already going (409 `run_in_progress`)
 * opens that one instead; other refusals become a toast with the server's reason.
 */
export function useStartReplayRun() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: (scenario: { id: string }) => startRun(scenario.id),
    onSuccess: async (run) => {
      queryClient.setQueryData(replayKeys.run(run.id), run);
      await queryClient.invalidateQueries({ queryKey: replayKeys.scenarios() });
      await navigate({ to: '/replay/runs/$runId', params: { runId: run.id } });
    },
    onError: async (error, scenario) => {
      if (error instanceof ApiError && error.status === 409 && error.code === 'run_in_progress') {
        const running = await runningRunOf(queryClient, scenario.id).catch(() => null);
        if (running) {
          toast.info('This replay is already running', { description: 'Opening it for you.' });
          await navigate({ to: '/replay/runs/$runId', params: { runId: running } });
        } else {
          toast.info('This replay is already running', {
            description: 'Open it from Sales replay to follow its progress.',
          });
        }
        return;
      }
      toast.error('The replay didn’t start', {
        description:
          error instanceof ApiError ? error.message : 'Check your connection and try again.',
      });
    },
  });
  return {
    start: (scenario: { id: string }) => mutation.mutate(scenario),
    pending: mutation.isPending,
    startingId: mutation.isPending ? mutation.variables?.id : undefined,
  };
}
